import { formatTimeOfDay } from '@lib/datetime'
import { DaySpan, segmentForDay } from './dayHeader'
import { dayPins, DayPin, DayRouteLine, dayRoutes, TimelineEvent, TimelineSegment, wholeTripPins } from './timeline'

/**
 * What the itinerary's map draws, worked out before Google Maps is involved:
 * the selected day's numbered pins and the lines between them, or the whole
 * trip's dots and the line through each day's first one. Structural types, like
 * the timeline's, so the module tests in node.
 */

export interface LatLng {
	lat: number
	lng: number
}

export interface MapEvent extends TimelineEvent {
	name: string
	startTime?: string | null
}

/** What the empty day's centre needs of a trip destination: its days and its place's coordinates. */
export interface MapDestination extends DaySpan {
	location: { placeDetails?: { geometry?: { location: LatLng } } | null }
}

export interface MapPin extends DayPin {
	dayIndex: number
	/** The marker's hover text: "Senso-ji Temple · 9:00 AM", or "Day 3 · Senso-ji Temple" on the whole trip. */
	title: string
}

export interface TripMapPin extends MapPin {
	/** The pin's destination colour; the day map's pins are all the brand orange. */
	colorIndex: number
}

export interface DayMapModel {
	mode: 'day'
	pins: MapPin[]
	/** The lines joining the pins: a cached route's encoded path, or null for a straight dashed line. */
	routes: DayRouteLine[]
	/** Where to look when the day has no pins: the destination's place, if it has coordinates. */
	emptyCenter: LatLng | null
}

export interface TripMapModel {
	mode: 'trip'
	pins: TripMapPin[]
	/** One line through each day's first pin; empty until two days have one. */
	path: LatLng[]
}

export type MapModel = DayMapModel | TripMapModel

/** The place of the destination covering a day, when it has coordinates. */
export const destinationCenter = (destinations: MapDestination[], dayIndex: number): LatLng | null => {
	const point = segmentForDay(destinations, dayIndex)?.location.placeDetails?.geometry?.location
	return point ? { lat: point.lat, lng: point.lng } : null
}

export const dayMapModel = <E extends MapEvent, S extends TimelineSegment>({
	dayIndex,
	events,
	segments,
	polylineOf,
	destinations,
}: {
	dayIndex: number
	/** The day's events, in order. */
	events: E[]
	segments: S[]
	polylineOf: (segment: S) => string | null
	destinations: MapDestination[]
}): DayMapModel => {
	const byId = new Map(events.map(event => [event.id, event]))
	const pins = dayPins(events).map(pin => {
		const event = byId.get(pin.eventId)
		const time = formatTimeOfDay(event?.startTime)
		const name = event?.name ?? ''
		return { ...pin, dayIndex, title: time ? `${name} · ${time}` : name }
	})
	return {
		mode: 'day',
		pins,
		routes: dayRoutes(events, segments, polylineOf),
		emptyCenter: pins.length === 0 ? destinationCenter(destinations, dayIndex) : null,
	}
}

export const tripMapModel = (events: MapEvent[], colorIndexOf: (dayIndex: number) => number): TripMapModel => {
	const byId = new Map(events.map(event => [event.id, event]))
	const pins = wholeTripPins(events).map(pin => ({
		...pin,
		title: `Day ${pin.dayIndex + 1} · ${byId.get(pin.eventId)?.name ?? ''}`,
		colorIndex: colorIndexOf(pin.dayIndex),
	}))
	const firstOfDay = new Map<number, LatLng>()
	pins.forEach(pin => {
		if (!firstOfDay.has(pin.dayIndex)) firstOfDay.set(pin.dayIndex, { lat: pin.lat, lng: pin.lng })
	})
	return { mode: 'trip', pins, path: firstOfDay.size > 1 ? [...firstOfDay.values()] : [] }
}
